import React, { useEffect, useRef, useState } from 'react';
import { forEach, map, transform, filter } from 'lodash';
import isNumber from 'lodash/isNumber';
import { translate, getTranslatedKPIName } from 'helpers/translateHelper';
import colors from 'theme/_colors.scss';
import { afterChartReDrawStringData } from '../../helpers';
import { renderToStaticMarkup } from 'react-dom/server';
import { TrendTooltip } from 'sharedComponents/ChartComponents/Trend';
import { ChartNoDataText } from 'sharedComponents/ChartComponents';
import { defaultBoostThreshold, DEFAULT_PRECISION } from 'helpers/constants';
import {
    getMeasurementUnitSymbol
} from 'helpers/unitHelper';
import ConditionIndicatorTooltip from './ConditionIndicatorTooltip';
import DcVoltageChartToolTipComponent from './DcVoltageChartToolTipComponent';
import {
    // CHART_PLOT_LINE_DASH_STYLE,
    CHART_TREND_EVENT_TOLERABLE_INDICATOR,
    CHART_TREND_EVENT_POOR_INDICATOR,
    CHART_TREND_EVENT_TOLERABLE_INDICATOR_TOOLTIP,
    CHART_TREND_EVENT_POOR_INDICATOR_TOOLTIP,
    CHART_CONDITION_TREND_STATUS_POOR,
    CHART_SERIES_CONDITION_INDICATOR_NAME,
    ALERT_LINE_COLOR,
    MARKER_FILL_COLOR,
    ALARM_LINE_COLOR,
    TREND_LINE_COLOR,
    CONDITION_STATUS_LABEL,
    TREND_TYPE_TOOLTIP_SERIES_NAME,
    CHART_CONDITION_TREND_STATUS_GOOD,
    CHART_CONDITION_TREND_KPI_TYPE,
    statusBarColors,
    CHART_CONDITION_TREND_STATUS_TOLERABLE,
    CHART_THRESHOLD_EVENT_POOR_INDICATOR_TOOLTIP,
    CHART_THRESHOLD_EVENT_TOLERABLE_INDICATOR_TOOLTIP,
    CHART_THRESHOLD_EVENT_POOR_INDICATOR,
    CHART_THRESHOLD_EVENT_TOLERABLE_INDICATOR,
    KPI_TYPE_THRESHOLD,
    CHART_CONDITION_TREND_SHORT_TERM_KPI_TYPE,
    CHART_CONDITION_TREND_LONG_TERM_KPI_TYPE,
    DRIVE_AIR_TEMPERATURE_NAME,
    DRIVE_DC_VOLTAGE_NAME,
    CHART_CONDITION_TREND_KPI_MAXIMUM_TYPE,
    CHART_CONDITION_TREND_KPI_RMS_TYPE,
    THRESHOLD_CURRENTOVERLOAD,
    CONDENSATION_RISK,
    DC_LINK_CAPACITOR,
    STATUS_BAR_TRANSPARENT_OPACITY,
    FORECAST_CI_SERIES_NAME,
    FORECAST_OVERLAY_NARROW_THRESHOLD,
    FORECAST_TOOLTIP_BOX_SHADOW,
    FORECAST_SIG_OFF,
    FORECAST_SIG_EMPTY,
    CONDITION_STATE_POOR,
    CONDITION_STATE_TOLERABLE,
    CONDITION_STATE_GOOD,
    CONDITION_STATE_UNKNOWN
} from './constants';
import { KPI_TYPE } from '../../constants';
import CurrentOverloadTooltip from './CurrentOverloadTooltip';
import DcLinkCapacitorTooltip from './DcLinkCapacitorTooltip';
import { acquireStatusBarTooltipHide, releaseStatusBarTooltipHide } from './statusBarTooltipListener';
import { compareDatesToDayPrecision, isUtcTimestamp } from 'helpers/dateHelper';
import { roundNumber } from 'helpers/numberHelper';
import EventsCustomTooltip from '../../EventsCustomTooltip';
import { IconInfo } from 'svgIcons/MotionPortalIcons';
import { formatDateTime } from 'helpers/dateTimeFormatter';
import { getXAxisLabelDateFormat } from '../../../helpersV2';

const PRINT_FONT_FAMILY = 'ABBvoice-Regular';
const TWO_DAYS_IN_MS = 2 * 24 * 60 * 60 * 1000;

const adjustAxisTranslation = (axis) => {
    if (!axis || typeof axis.min !== 'number' || typeof axis.max !== 'number' || typeof axis.len !== 'number') {
        return;
    }

    const minOff = axis.minPointOffset || 0;

    if (
        axis.max - axis.min > 0 &&
        axis.len > 0 &&
        axis.pointRangePadding > minOff
    ) {
        axis.pointRangePadding = minOff;
        axis.transA =
            axis.staticScale ||
            axis.len / (axis.max - axis.min + minOff || 1);
        axis.minPixelPadding = axis.transA * minOff;
    }
};

const getPrintableTitle = (assetDetails, chartTitle) => {
    return renderToStaticMarkup(
        <div className='asset-title-chart' id='trend-chart-title'>
            <span>{`${assetDetails?.details?.assetName} - ${chartTitle}`}</span>
        </div>
    );
};

const getChartConfig = (removeKPIData, title, assetDetails, conditionTrendIndicators, currentOverloadPrecision = 2) => {

    return {
        currentOverloadPrecision,
        title: {
            text: null
        },
        chart: {
            zoomType: 'x',
            height: null,
            events: {
                render: function (chartInstance) {
                    // Skip expensive render operations during print
                    if (this._renderEventDisabled) {
                        return;
                    }

                    afterChartReDrawStringData(chartInstance.target.container.id, removeKPIData);
                    const chart = this;
                    const series = chart?.series;
                    // Add custom events arrow icon from dataLabels
                    if (series?.length) {
                        if (chart?.customEvents?.length) {
                            chart?.customEvents.map((_, index) => {
                                // Remove the arrow events icon
                                chart.customEvents[index].hide();
                            });
                        }
                        const getSeries = series.find((s) => s.name === CHART_SERIES_CONDITION_INDICATOR_NAME);
                        chart.customEvents = [];
                        getSeries?.points.forEach((point, index) => {
                            const formatter = getSeries?.options?.data?.[index]?.dataLabels?.formatter.call({ point });
                            const svgWidth = 16, svgHeight = 32;
                            const label = chart?.renderer?.image(
                                'data:image/svg+xml;base64,' + btoa(formatter),
                                point.plotX + chart.plotLeft - svgWidth / 2,
                                point.plotY + chart.plotTop - svgHeight,
                                svgWidth,
                                svgHeight
                            ).attr({
                                zIndex: 10
                            }).add();

                            chart.customEvents.push(label);
                        });
                        // Draw an arrow indicator point
                        if (chart?.customPaths?.length) {
                            chart?.customPaths.map((_, index) => {
                                // Remove a line data from chart when come out of chart area
                                chart.customPaths[index]['line'].hide();
                                chart.customPaths[index]['arrow'].hide();
                            });
                        }
                        chart.customPaths = [];
                        conditionTrendIndicators?.forEach((event, index) => {
                            const detectedTrend = event?.detectedTrend;
                            if (detectedTrend && chart?.xAxis?.length > 0 && chart?.yAxis?.length > 0) {
                                const xMin = chart?.xAxis?.[0]?.min;
                                const xMax = chart?.xAxis?.[0]?.max;
                                const yMin = chart?.yAxis?.[0]?.min;
                                const yMax = chart?.yAxis?.[0]?.max;
                                const arrowX0 = new Date(detectedTrend.x0).getTime();
                                const arrowX1 = new Date(detectedTrend.x1).getTime();
                                const arrowY0 = detectedTrend.y0;
                                const arrowY1 = detectedTrend.y1;
                                // Show arrow only within the chart area range (X and Y axis)
                                const isArrowVisible = arrowX0 >= xMin && arrowX0 <= xMax &&
                                    arrowX1 >= xMin && arrowX1 <= xMax &&
                                    arrowY0 >= yMin && arrowY0 <= yMax &&
                                    arrowY1 >= yMin && arrowY1 <= yMax;
                                if (isArrowVisible) {
                                    // set x0, x1, y0 and y1 values to chart series.
                                    const ax = chart?.xAxis?.[0]?.toPixels(arrowX0),
                                        ay = chart?.yAxis?.[0]?.toPixels(arrowY0),
                                        bx = chart?.xAxis?.[0]?.toPixels(arrowX1),
                                        by = chart?.yAxis?.[0]?.toPixels(arrowY1),
                                        color = statusBarColors[event.newCondition];

                                    if (chart?.customPaths && !chart.customPaths[index]) {

                                        // Arrowhead calculation
                                        const arrowLength = 10;
                                        const arrowAngle = Math.PI / 6; // 30 degrees
                                        const angle = Math.atan2(by - ay, bx - ax);
                                        const x1 = bx - arrowLength * Math.cos(angle - arrowAngle);
                                        const y1 = by - arrowLength * Math.sin(angle - arrowAngle);
                                        const x2 = bx - arrowLength * Math.cos(angle + arrowAngle);
                                        const y2 = by - arrowLength * Math.sin(angle + arrowAngle);

                                        chart.customPaths[index] = {
                                            // Draw line
                                            line: chart?.renderer?.path(['M', 0, 0, 'L', 0, 0])?.attr({
                                                stroke: color,
                                                'stroke-width': 2,
                                                zIndex: 1001,
                                            }).attr({
                                                d: ['M', ax, ay, 'L', bx, by]
                                            }).add(),
                                            // Draw arrowhead
                                            arrow: chart?.renderer.path([
                                                'M', bx, by,
                                                'L', x1, y1,
                                                'L', x2, y2,
                                                'Z'
                                            ]).attr({
                                                fill: color,
                                                zIndex: 1001
                                            }).add()
                                        };
                                    }
                                }
                            }
                        });
                    }
                    const kpiStatesSeries = this.series.find(s => s.name === 'KPI states');
                    if (kpiStatesSeries && kpiStatesSeries?.fullData?.length && this.xAxis?.length > 0) {
                        const xMin = this.xAxis[0]?.min;
                        const xMax = this.xAxis[0]?.max;
                        const filteredData = kpiStatesSeries.fullData.filter(d => d.x >= xMin && d.x <= xMax);
                        kpiStatesSeries.setData(filteredData, false);
                    }
                    if (!this?.pointer?.onContainerMouseLeaveOriginal) {
                        this.pointer.onContainerMouseLeaveOriginal = this.pointer?.onContainerMouseLeave;
                    }
                    // Remove lingering status bar tooltip on render (e.g., after asset switch)
                    const existingStatusTooltip = document.body.querySelector('.status-bar-tooltip-fixed');
                    if (existingStatusTooltip) {
                        existingStatusTooltip.remove();
                    }
                },
                beforePrint: function () {
                    const printableTitle = getPrintableTitle(assetDetails, title);
                    if (this.exportSVGElements?.length > 0) {
                        this.exportSVGElements[0]?.hide();
                    }

                    // Temporarily disable expensive render event during print
                    this._renderEventDisabled = true;

                    // Hide custom elements to avoid re-rendering
                    if (this.customEvents?.length) {
                        this.customEvents.forEach(event => event?.hide());
                    }
                    if (this.customPaths?.length) {
                        this.customPaths.forEach(path => {
                            path?.line?.hide();
                            path?.arrow?.hide();
                        });
                    }

                    this.setTitle({
                        text: printableTitle,
                        useHTML: true,
                        align: 'left',
                        x: 50,
                        y: -10,
                    });

                    // Use direct CSS updates instead of this.update() to avoid full re-render
                    const legendElement = this.legend?.group?.element;
                    if (this.xAxis?.length > 0) {
                        const xAxisLabels = this.xAxis[0]?.labelGroup?.element;
                        if (xAxisLabels?.style) {
                            xAxisLabels.style.fontFamily = PRINT_FONT_FAMILY;
                        }
                    }

                    this.yAxis?.forEach(axis => {
                        const yAxisLabels = axis.labelGroup?.element;
                        if (yAxisLabels?.style) {
                            yAxisLabels.style.fontFamily = PRINT_FONT_FAMILY;
                        }
                    });

                    if (legendElement?.style) {
                        legendElement.style.fontFamily = PRINT_FONT_FAMILY;
                    }
                },
                afterPrint: function () {
                    if (this.exportSVGElements?.length > 0) {
                        this.exportSVGElements[0]?.show();
                    }
                    this.setTitle({
                        text: '',
                        useHTML: false,
                    });

                    // Re-enable render event
                    this._renderEventDisabled = false;

                    // Restore custom elements
                    if (this.customEvents?.length) {
                        this.customEvents.forEach(event => event?.show());
                    }
                    if (this.customPaths?.length) {
                        this.customPaths.forEach(path => {
                            path?.line?.show();
                            path?.arrow?.show();
                        });
                    }
                },
                load: function () {
                    acquireStatusBarTooltipHide();
                    this._statusBarTooltipHideAcquired = true;
                },
                destroy: function () {
                    if (this._statusBarTooltipHideAcquired) {
                        releaseStatusBarTooltipHide();
                        this._statusBarTooltipHideAcquired = false;
                    }
                }
            },
            style: {
                fontFamily: PRINT_FONT_FAMILY,
            },
            spacingTop: 70,
            marginRight: 30,
        },
        boost: {
            allowForce: false //Workaround for highchart bug. Allows correctly disabling boost, when zooming in.
        },
        accessibility: {
            enabled: true,
            keyboardNavigation: {
                enabled: true,
                wrapAround: true
            }
        },
        time: {
            useUTC: false
        },
        xAxis: {
            type: 'datetime',
            id: 'x',
            dateTimeLabelFormats: {
                millisecond: '%H:%M:%S',
                day: '%e. %b',
                month: '%b \'%y'
            },
            crosshair: {
                enabled: true,
                width: 2,
                color: colors.tundora,
            },
            labels: {
                rotation: 50,
                color: '#000000',
                style: {
                    fontWeight: 'bold',
                    fontColor: '#000000'
                },
            },
            lineColor: '#000000',
            lineWidth: 2,
            tickPixelInterval: 25,
            tickColor: '#000000',
            tickWidth: 2,

        },
        legend: {
            useHTML: true,
            layout: 'horizontal',
            align: 'right',
            itemStyle: {
                fontFamily: PRINT_FONT_FAMILY,
                fontSize: '14px',
            },
            itemDistance: 25,
            itemMarginTop: 10,
            enabled: false,
            symbolRadius: 0,
            squareSymbol: false,
            symbolHeight: 8,
            symbolWidth: 8
        },
        credits: {
            enabled: false,
        },
        tooltip: {
            shared: true,
            valueDecimals: 2,
            useHTML: true,
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            shadow: false,
            padding: 0,
            borderWidth: 0,
            hideDelay: 0,
            dateTimeLabelFormats: {
                day: '%d.%m.%Y<br/>%H:%M',
                hour: '%d.%m %H:%M',
            },
        },
        plotOptions: {
            series: {
                turboThreshold: 0,
                animation: false,
                boostThreshold: 500,
            },
            column: {
                boostThreshold: 1000,
            }
        },
        navigation: {
            buttonOptions: {
                align: 'right',
            },
            menuStyle: {
                borderColor: '#fff',
                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.08)',
                borderRadius: '6px'
            },
            menuItemStyle: {
                padding: '6px 12px',
                fontSize: '14px',
                fontFamily: PRINT_FONT_FAMILY,
            },
            menuItemHoverStyle: {
                backgroundColor: '#ebebeb',
                color: '#1f1f1f'
            }
        },
        lang: {
            printChart: translate('ABB.Powertrain.Frontend.printChart'),
            downloadPNG: translate('ABB.Powertrain.Frontend.saveAsImage'),
        },
        noData: {
            useHTML: true
        },
        exporting: {
            enabled: true,
            scale: 1.5,
            sourceWidth: 1100,
            sourceHeight: 500,
            fallbackToExportServer: false,
            allowHTML: true,
            buttons: {
                contextButton: {
                    menuItems: ['downloadPNG', 'printChart'],
                    verticalAlign: 'top',
                    x: 0,
                    y: -70
                }
            },
            chartOptions: {
                chart: {
                    marginRight: 40,
                },
                title: {
                    text: `${assetDetails?.details?.assetName} - ${title}`,
                    align: 'left',
                    x: 50,
                    y: -10,
                    style: {
                        fontFamily: 'Verdana, sans-serif',
                        fontWeight: 'bold'
                    }
                },
                xAxis: {
                    labels: {
                        style: {
                            fontFamily: 'Verdana, sans-serif',
                        }
                    }
                },
                yAxis: {
                    labels: {
                        style: {
                            fontFamily: 'Verdana, sans-serif',
                        }
                    }
                },
                lang: {
                    noData: renderToStaticMarkup(
                        <div style={{
                            display: 'block',
                            width: '100%',
                            maxWidth: '1200px',
                            textAlign: 'center',
                            padding: '20px 60px',
                            boxSizing: 'border-box',
                            margin: '0 auto'
                        }}>
                            <strong style={{
                                display: 'block',
                                marginBottom: '26px',
                                fontSize: '14px',
                                fontFamily: 'Verdana, sans-serif',
                                fontWeight: 'bold'
                            }}>{translate('ABB.Powertrain.Frontend.chartNoMeasurementData')}</strong>
                            <span style={{
                                display: 'block',
                                fontSize: '12px',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'normal',
                                maxWidth: '100%',
                                fontFamily: 'Verdana, sans-serif',
                            }}>{translate('ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuide')}</span>
                        </div>
                    )
                },
                noData: {
                    useHTML: true
                }
            }
        }
    };
};

const getDcVoltageTrend = (conditionTrendsData, date, isViewWithinTwoDays) => {
    const dcVoltageStatusTrend = conditionTrendsData?.find(trend => trend.type === CHART_CONDITION_TREND_KPI_TYPE);
    let matchedTrend = dcVoltageStatusTrend?.values?.find(val => new Date(val?.timestamp).getTime() === date);

    if (!matchedTrend && !isViewWithinTwoDays) {
        matchedTrend = dcVoltageStatusTrend?.values?.find(val => {
            return compareDatesToDayPrecision(new Date(val?.timestamp), new Date(date));
        });
    }

    return matchedTrend;
};

const buildDcVoltageMeasurement = (config, measurement, date, conditionTrendsData, isViewWithinTwoDays) => {
    const dcVoltageTrend = getDcVoltageTrend(conditionTrendsData, date, isViewWithinTwoDays);
    return {
        x: date,
        y: measurement?.value ? measurement.value + config.offset : measurement.value,
        timeStamp: dcVoltageTrend?.timestamp,
        faultCount: dcVoltageTrend?.faultCount ?? 0,
        warningCount: dcVoltageTrend?.warningCount ?? 0,
        events: dcVoltageTrend?.events ?? [],
        trendType: CHART_CONDITION_TREND_KPI_TYPE,
        condition: dcVoltageTrend?.value
    };
};

const buildDcLinkCapacitorMeasurement = (config, measurement, date, conditionTrendsData) => {
    const dcLinkCapacitorStatusTrend = conditionTrendsData?.find(t => t.type === CHART_CONDITION_TREND_KPI_TYPE);
    const dcLinkCapacitorTrend = dcLinkCapacitorStatusTrend?.values?.find(v => new Date(v?.timestamp).getTime() === date);
    return {
        x: date,
        y: measurement?.value ? measurement.value + config.offset : measurement.value,
        isDcLinkCapacitor: true,
        condition: dcLinkCapacitorTrend?.value,
        chargingCycleNumber: dcLinkCapacitorTrend?.chargingCycleNumber,
        isKpiTransparent: dcLinkCapacitorTrend?.isKpiTransparent,
        min: measurement?.min,
        max: measurement?.max
    };
};

const getMeasurements = (config, measurements, showRangeData, conditionTrendsData, isViewWithinTwoDays) => {
    const averages = [];
    const rangedata = [];
    const isDcVoltage = config?.displayName?.toLowerCase().includes(DRIVE_DC_VOLTAGE_NAME);
    const isDcLinkCapacitor = config?.measurementTypeKey?.includes(DC_LINK_CAPACITOR);

    forEach(measurements, (measurement) => {
        if (measurement.timestamp === null) {
            return;
        }
        const date = new Date(measurement.timestamp).getTime();

        if (isDcVoltage) {
            averages.push(buildDcVoltageMeasurement(config, measurement, date, conditionTrendsData, isViewWithinTwoDays));
        } else if (isDcLinkCapacitor) {
            averages.push(buildDcLinkCapacitorMeasurement(config, measurement, date, conditionTrendsData));
        } else {
            averages.push([date, measurement?.value ? measurement.value + config.offset : measurement.value]);
        }

        if (showRangeData) {
            rangedata.push([
                date,
                isNumber(measurement?.min) ? measurement.min + config.offset : null,
                isNumber(measurement?.max) ? measurement.max + config.offset : null
            ]);
        }
    });

    return { averages, rangedata };
};

const getUserLimitsSeries = (userLimits, config, seriesDataPoints) => {
    let series = [];
    if (userLimits && userLimits.length) {
        series = map(userLimits, (ul, index) => {
            return {
                name: ul.label || config.displayName || '',
                id: `ul_${index}`,
                showInLegend: false,
                color: ul.color || colors.teak,
                marker: { enabled: false },
                lineWidth: 1,
                dashStyle: 'Solid',
                boostThreshold: 0,
                data: transform(seriesDataPoints, (acc, sd) => {
                    const [date, value] = sd;
                    if (value !== null) {
                        acc.push([date, ul.value]);
                    }
                }, []),
                unit: config && config.unit ? config.unit : '',
                tooltip: {
                    valueSuffix: ` ${config && config.unit ? config.unit : ''}`,
                },
            };
        });
    }
    return series;
};

// const getPlotLines = (plotLineList) => {
//     let bandLineList = [];
//     if (plotLineList && plotLineList?.length) {
//         bandLineList = map(plotLineList, (ul) => {
//             return {
//                 value: ul?.value,
//                 color: ul?.color,
//                 dashStyle: CHART_PLOT_LINE_DASH_STYLE,
//                 width: 1.2,
//                 zIndex: 10
//             };

//         });
//     }
//     return bandLineList;
// };

// Commented, As not needed background color for plot lines
// const getPlotBandsWithMaxMin = (plotBandsList, yAxis, mainAxis) => {

//     if (plotBandsList?.length) {
//         map(plotBandsList, (ul) => {
//             mainAxis?.addPlotBand({
//                 from: ul?.from === null ? yAxis?.min : ul?.from,
//                 to: ul?.to === null ? yAxis?.max : ul?.to,
//                 color: ul?.color
//             });

//         });
//     }
// };
const getCheckConditionEventsLabel = (conditionIndicator) => {
    const newCondition = conditionIndicator?.newCondition;
    return (
        newCondition === CHART_CONDITION_TREND_STATUS_TOLERABLE ||
        newCondition === CHART_CONDITION_TREND_STATUS_POOR
    );
};

const getThresholdLabel = (isPoor, ul) => {
    // Check the KPI Type
    const isKpiThreshold = ul?.kpiType === KPI_TYPE_THRESHOLD;
    return {
        enabled: false,
        useHTML: true,
        formatter: function () {
            // Use Threshold icons if KPI Type is Threshold
            if (isKpiThreshold) {
                return isPoor ? CHART_THRESHOLD_EVENT_POOR_INDICATOR : CHART_THRESHOLD_EVENT_TOLERABLE_INDICATOR;
            }
            // Use Trend icons otherwise
            return isPoor ? CHART_TREND_EVENT_POOR_INDICATOR : CHART_TREND_EVENT_TOLERABLE_INDICATOR;
        }
    };
};

const getConditionIndicators = (condtionIndicators, isDcVoltage) => {
    if (condtionIndicators?.length && !isDcVoltage) {
        return filter(condtionIndicators, indicator => getCheckConditionEventsLabel(indicator)).map((ul) => {
            return {
                y: ul?.trendValue,
                x: new Date(ul?.timestamp)?.getTime(),
                dataLabels: getThresholdLabel(ul?.newCondition === CHART_CONDITION_TREND_STATUS_POOR, ul),
                color: statusBarColors[ul?.newCondition],
            };
        });
    }
};

const getStatusBarData = (trendValues, kpiType) => {
    const getKpiTypeTrend = trendValues?.find(trend => trend.type === kpiType);
    if (getKpiTypeTrend) {
        return map(getKpiTypeTrend?.values, (ul) => {
            return {
                y: 1, // Able to adjust status bar height by using y axis value
                x: new Date(ul?.timestamp)?.getTime(),
                color: statusBarColors[ul?.value],
                opacity: ul?.isKpiTransparent ? STATUS_BAR_TRANSPARENT_OPACITY : 1,
                events: ul?.events ?? [],
                condition: ul?.value,
                faultCount: ul?.faultCount ?? 0,
                warningCount: ul?.warningCount ?? 0,
                isFromConditionTrendsHourly: !!getKpiTypeTrend?.isFromConditionTrendsHourly,
            };
        });
    }
};

const getPlotLinesData = (plotLineValues) => {
    return map(plotLineValues, (ul) => {
        return {
            y: ul?.value,
            x: new Date(ul?.timestamp)?.getTime(),
        };
    });
};

const getConditionImage = (isPoor, kpiType) => {
    // Check the KPI Type
    const isKpiThreshold = kpiType === KPI_TYPE_THRESHOLD;
    // Return the appropriate icon based on the KPI Type and isPoor condition
    if (isKpiThreshold) {
        return isPoor ? CHART_THRESHOLD_EVENT_POOR_INDICATOR_TOOLTIP : CHART_THRESHOLD_EVENT_TOLERABLE_INDICATOR_TOOLTIP;
    }
    return isPoor ? CHART_TREND_EVENT_POOR_INDICATOR_TOOLTIP : CHART_TREND_EVENT_TOLERABLE_INDICATOR_TOOLTIP;
};

const getConditionTypeLabel = (KpiType) => {
    return translate(`ABB.Powertrain.Frontend.${CONDITION_STATUS_LABEL[KpiType]}`);
};

const getConditionIndicatorsV2 = (condtionIndicators, isDcVoltage) => {
    if (condtionIndicators?.length && !isDcVoltage) {
        return filter(condtionIndicators, indicator => getCheckConditionEventsLabel(indicator)).map((ul) => {
            return {
                y: ul?.trendValue,
                x: new Date(ul?.timestamp)?.getTime(),
                customObject: getConditionImage(ul?.newCondition === CHART_CONDITION_TREND_STATUS_POOR, ul?.kpiType),
                KpiTypeLabel: getConditionTypeLabel(ul?.kpiType)
            };
        });
    }
};

// const generateMarkerData = (averages, limitValue) => {
//     if (averages?.length) {
//         return averages.map((average) => {
//             return [average[0], limitValue];
//         });
//     }
// };

const calculateChartHeight = (isShortTermBarVisible, isLongTermBarVisible) => {
    if (isShortTermBarVisible && isLongTermBarVisible) {
        return '75%';
    } else if (isShortTermBarVisible || isLongTermBarVisible) {
        return '78%';
    }
    return '83%';
};

const calculateStatusBarTop = (isShortTermBarVisible, isLongTermBarVisible, type) => {
    if (type === CHART_CONDITION_TREND_KPI_TYPE) {
        if (isShortTermBarVisible && isLongTermBarVisible) {
            return '79.8%';
        } else if (isShortTermBarVisible || isLongTermBarVisible) {
            return '84.6%';
        }
        return '92.6%';
    } else if (type === CHART_CONDITION_TREND_SHORT_TERM_KPI_TYPE) {
        if (isShortTermBarVisible && isLongTermBarVisible) {
            return '87.8%';
        }
        return '94.6%';
    }
    else if (type === CHART_CONDITION_TREND_LONG_TERM_KPI_TYPE) {
        if (isShortTermBarVisible && isLongTermBarVisible) {
            return '95.8%';
        }
        return '94.6%';
    }
};

const processCurrentOverload = (acc, item, conditionTrendsData, conditionTrendIndicators) => {
    // Find KpiMaximum and KpiRms for series
    const kpiStatusBar = conditionTrendsData?.find(trend => trend.type === CHART_CONDITION_TREND_KPI_TYPE);
    const kpiMaximum = conditionTrendsData?.find(trend => trend.type === CHART_CONDITION_TREND_KPI_MAXIMUM_TYPE);
    const kpiRms = conditionTrendsData?.find(trend => trend.type === CHART_CONDITION_TREND_KPI_RMS_TYPE);
    const trafficBarData = Array.isArray(kpiStatusBar?.values)
        ? kpiStatusBar.values.map(v => ({
            x: new Date(v.timestamp).getTime(),
            y: 1,
            color: statusBarColors[v.value]
        }))
        : [];
    const hasStatusBarData = trafficBarData?.length > 0;

    // Max load and Quadratic mean load series
    const normalizeVal = (v) => {
        if (v === null || v === undefined || Number.isNaN(Number(v))) { return null; }
        return Math.round(Number(v) * 100);
    };
    const maxLoadSeries = Array.isArray(kpiMaximum?.values)
        ? kpiMaximum?.values.map(v => {
            const val = normalizeVal(v?.value);
            return [new Date(v?.timestamp).getTime(), val];
        })
        : [];
    const quadMeanSeries = Array.isArray(kpiRms?.values)
        ? kpiRms?.values.map(v => {
            const val = normalizeVal(v?.value);
            return [new Date(v?.timestamp).getTime(), val];
        })
        : [];

    const noDataForCurrentOverload =
        (!Array.isArray(kpiMaximum?.values) || kpiMaximum?.values?.length === 0) &&
        (!Array.isArray(kpiRms?.values) || kpiRms?.values?.length === 0);

    if (noDataForCurrentOverload) {
        return acc;
    }

    // Helper for single point marker
    // const hasSingleValidPoint = (series) =>
    //     Array.isArray(series) && series.filter(pt => pt[1] !== null && pt[1] !== undefined).length === 1;

    // Y Axis for chart
    acc.yAxis.push({
        min: 0,
        endOnTick: true,
        startOnTick: true,
        top: '0%',
        height: '82%',
        labels: {
            format: '{value} %',
            style: { color: item.color },
        },
        id: 'current_load_y',
        title: { text: '', enabled: true, style: { color: colors.tundora } },
        opposite: false,
        visible: true,
    });

    // Status bar axis (only visible if data)
    acc.yAxis.push({
        top: '92%',
        height: '19px',
        offset: 0,
        id: 'current_load_status_bar_y',
        title: {
            text: hasStatusBarData ? translate('ABB.Powertrain.Frontend.thresholdCrossingStatus') : null,
            align: 'low',
            x: 20,
            y: -25,
            style: {
                fontFamily: PRINT_FONT_FAMILY,
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '12px',
                whiteSpace: 'nowrap',
                transform: 'rotate(0deg)'
            },
        },
        labels: { enabled: false },
        gridLineWidth: 0,
        lineWidth: 0,
        tickLength: 0,
        min: 0,
        max: 1,
        endOnTick: false,
        startOnTick: false,
        visible: hasStatusBarData
    });

    // Max load line
    acc.seriesData.push({
        name: 'Max load',
        data: maxLoadSeries,
        type: 'line',
        color: '#E87FE8',
        yAxis: 'current_load_y',
        tooltip: { valueSuffix: ' %' },
        marker: { enabled: true, radius: 2 },
        lineWidth: 2,
        id: 'max-load',
        zIndex: 8,
        clip: false,
        visible: true,
        connectNulls: false,
        gapSize: 3600000,
        gapUnit: 'value'
    });

    // Quadratic mean load line
    acc.seriesData.push({
        name: 'Quadratic mean load',
        data: quadMeanSeries,
        type: 'line',
        color: '#1F5A99',
        yAxis: 'current_load_y',
        tooltip: { valueSuffix: ' %' },
        marker: { enabled: true, radius: 2 },
        lineWidth: 2,
        id: 'quad-mean-load',
        zIndex: 10,
        clip: false,
        visible: true,
        connectNulls: false,
        gapSize: 3600000,
        gapUnit: 'value'
    });

    // Status bar (always present, but empty if no data)
    acc.seriesData.push({
        name: 'KPI states',
        type: 'column',
        yAxis: 'current_load_status_bar_y',
        xAxis: 'x',
        boostThreshold: 0,
        data: hasStatusBarData ? trafficBarData : [],
        pointPadding: 0,
        groupPadding: 0,
        borderWidth: 0,
        threshold: 0,
        pointPlacement: 0,
        cropThreshold: 10000,
        minPointLength: 1,
        enableMouseTracking: false,
        states: {
            inactive: { enabled: false },
            hover: { enabled: false }
        },
        visible: hasStatusBarData
    });

    const coMaxMap = new Map(maxLoadSeries.filter(([, y]) => y !== null));
    const coRmsMap = new Map(quadMeanSeries.filter(([, y]) => y !== null));
    const nearestCOY = (ts) => {
        let best = null, minDiff = Infinity;
        for (const [x, y] of [...coMaxMap, ...coRmsMap]) {
            const d = Math.abs(x - ts);
            if (d < minDiff) { minDiff = d; best = y; }
        }
        return best;
    };
    const resolveCOPointY = (ts) => coMaxMap.get(ts) ?? coRmsMap.get(ts) ?? nearestCOY(ts);
    const mapCOIndicatorY = (pts) =>
        pts?.map(pt => ({ ...pt, y: resolveCOPointY(pt.x) ?? pt.y }));

    acc.seriesData.push({
        data: mapCOIndicatorY(getConditionIndicatorsV2(conditionTrendIndicators)),
        name: TREND_TYPE_TOOLTIP_SERIES_NAME,
        color: ALERT_LINE_COLOR,
        boostThreshold: 0,
        xAxis: 'x',
        id: `${crypto.randomUUID()}`,
        yAxis: 'current_load_y',
        linkedTo: ':previous',
        lineWidth: 0,
        states: {
            hover: {
                lineWidthPlus: 0,
            },
        },
        marker: {
            enabled: false,
            radius: 1,
            symbol: 'circle',
            fillColor: MARKER_FILL_COLOR,
            lineColor: ALERT_LINE_COLOR,
            lineWidth: 0.2,
        },
        tooltip: {
            valueSuffix: ' %',
        },
    });

    acc.seriesData.push({
        name: CHART_SERIES_CONDITION_INDICATOR_NAME,
        id: `${crypto.randomUUID()}`,
        yAxis: 'current_load_y',
        boostThreshold: 0,
        lineWidth: 0,
        states: {
            hover: {
                lineWidthPlus: 0,
            },
        },
        marker: {
            enabled: false,
            radius: 1,
            symbol: 'circle',
            fillColor: MARKER_FILL_COLOR,
            lineColor: '#000000',
            lineWidth: 0.2,
        },
        xAxis: 'x',
        tooltip: {
            enabled: true,
            valueSuffix: ' %',
        },
        data: mapCOIndicatorY(getConditionIndicators(conditionTrendIndicators)),
    });
};

const addAlertLines = (acc, yAxisId, params, convertedUnit, unit) => {
    const { highErrorValue, lowErrorValue, highAlertLimitValue, lowAlertValue } = params;

    const addLine = (valueData, nameKey, color) => {
        if (valueData?.length) {
            acc.seriesData.push({
                type: 'line',
                data: getPlotLinesData(valueData),
                name: translate(nameKey),
                color: color,
                xAxis: 'x',
                id: `${crypto.randomUUID()}`,
                yAxis: yAxisId,
                linkedTo: ':previous',
                lineWidth: 1,
                boostThreshold: 0,
                states: { hover: { lineWidthPlus: 0 } },
                marker: { enabled: true, radius: 1, symbol: 'circle', fillColor: MARKER_FILL_COLOR, lineColor: color, lineWidth: 0.5 },
                tooltip: { valueSuffix: ` ${convertedUnit || unit}` },
                zIndex: 10
            });
        }
    };

    addLine(highErrorValue, 'ABB.Powertrain.Frontend.conditionTrendTooltipPoor', ALARM_LINE_COLOR);
    addLine(lowErrorValue, 'ABB.Powertrain.Frontend.conditionTrendTooltipPoor', ALARM_LINE_COLOR);
    addLine(highAlertLimitValue, 'ABB.Powertrain.Frontend.conditionTrendTooltipTolerable', ALERT_LINE_COLOR);
    addLine(lowAlertValue, 'ABB.Powertrain.Frontend.conditionTrendTooltipTolerable', ALERT_LINE_COLOR);
};

const getYAxisTitleText = (isCondensationRiskKpi, isDcLinkCapacitorKpi, convertedUnit, unit, name) => {
    if (isCondensationRiskKpi) {
        return translate('ABB.Powertrain.Frontend.condensationRiskDewPointMarginLabel', { unit: convertedUnit || unit });
    }
    if (isDcLinkCapacitorKpi) {
        return translate('ABB.Powertrain.Frontend.dcLinkCapacitorYAxisLabel');
    }
    return name;
};

const buildMainYAxisConfig = (locals, params) => {
    const {
        config, isDcVoltage, convertedUnit, unit, isCondensationRiskKpi,
        isDcLinkCapacitorKpi, name, itemColor, yAxisId, index
    } = locals;
    const { yAxisTitleVisible, isShortTermBarVisible, isLongTermBarVisible } = params;

    const isThreshold = config?.measurementTypeKey?.includes(KPI_TYPE.Threshold);
    const isFullHeightLayout = isDcVoltage || isDcLinkCapacitorKpi;
    const layoutConfig = isThreshold || isDcVoltage ? {
        top: '0%',
        height: isFullHeightLayout ? '82%' : calculateChartHeight(isShortTermBarVisible, isLongTermBarVisible),
        offset: 0,
    } : {};

    const titleText = getYAxisTitleText(isCondensationRiskKpi, isDcLinkCapacitorKpi, convertedUnit, unit, name);

    return {
        ...layoutConfig,
        labels: {
            format: '{value} ' + (convertedUnit || unit),
            style: {
                color: itemColor,
            },
        },
        id: yAxisId,
        title: {
            text: titleText,
            enabled: yAxisTitleVisible,
            style: {
                color: itemColor,
            },
        },
        // plotLines: getPlotLines(warningLines),
        opposite: index % 2 !== 0,
        visible: true,
        // Commented, As not needed background color for plot lines
        // events: {
        //     setExtremes: function (e) {
        //         if (!isPlotBandAdded) {
        //             getPlotBandsWithMaxMin(warningLevels, e?.target, this);
        //             isPlotBandAdded = true;
        //         }
        //     },
        // },
    };
};

const processRangeDataAndStatusBars = (acc, params, locals) => {
    const {
        isShortTermBarVisible, isLongTermBarVisible, conditionTrendsData, valueType, isViewWithinTwoDays
    } = params;
    const {
        isDcVoltage, isDcLinkCapacitorKpi, thresholdBarYAxisId, shortTermBarYAxisId, longTermBarYAxisId,
        isHourlyDcVoltageData, name, averages, convertedUnit, unit, precision,
        yAxisId, rangedata, itemColor
    } = locals;
    const isFullHeightLayout = isDcVoltage || isDcLinkCapacitorKpi;

    const getStatusBarTitle = () => {
        if (isDcVoltage) { return translate('ABB.Powertrain.Frontend.relatedEventsLabel'); }
        if (isDcLinkCapacitorKpi) { return translate('ABB.Powertrain.Frontend.dcLinkCapacitorOverallStatusLabel'); }
        return translate('ABB.Powertrain.Frontend.thresholdCrossingStatus');
    };

    acc.yAxis.push({
        top: isFullHeightLayout ? '92%' : calculateStatusBarTop(isShortTermBarVisible, isLongTermBarVisible, CHART_CONDITION_TREND_KPI_TYPE),
        height: '19px',
        offset: 0,
        id: thresholdBarYAxisId,
        title: {
            text: getStatusBarTitle(),
            align: 'low',
            x: 20,
            y: -20,
            style: {
                fontFamily: PRINT_FONT_FAMILY,
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '12px',
                whiteSpace: 'nowrap',
                transform: 'rotate(0deg)'
            },
        },
        labels: { enabled: false },
        gridLineWidth: 0,
        lineWidth: 0,
        tickLength: 0,
    });

    if (isShortTermBarVisible) {
        acc.yAxis.push({
            top: calculateStatusBarTop(isShortTermBarVisible, isLongTermBarVisible, CHART_CONDITION_TREND_SHORT_TERM_KPI_TYPE),
            height: '19px',
            offset: 0,
            id: shortTermBarYAxisId,
            title: {
                text: translate('ABB.Powertrain.Frontend.conditionTrendTypeShortTermLabel'),
                align: 'low',
                x: 20,
                y: -20,
                style: {
                    fontFamily: PRINT_FONT_FAMILY,
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '12px',
                    whiteSpace: 'nowrap',
                    transform: 'rotate(0deg)'
                },
            },
            labels: { enabled: false },
            gridLineWidth: 0,
            lineWidth: 0,
            tickLength: 0,
        });
    }

    if (isLongTermBarVisible) {
        acc.yAxis.push({
            top: calculateStatusBarTop(isShortTermBarVisible, isLongTermBarVisible, CHART_CONDITION_TREND_LONG_TERM_KPI_TYPE),
            height: '19px',
            offset: 0,
            id: longTermBarYAxisId,
            title: {
                text: translate('ABB.Powertrain.Frontend.conditionTrendTypeLongTermLabel'),
                align: 'low',
                x: 20,
                y: -20,
                style: {
                    fontFamily: PRINT_FONT_FAMILY,
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '12px',
                    whiteSpace: 'nowrap',
                    transform: 'rotate(0deg)'
                },
            },
            labels: { enabled: false },
            gridLineWidth: 0,
            lineWidth: 0,
            tickLength: 0,
        });
    }

    acc.seriesData.push({
        name: `${name}${valueType ? ` (${valueType})` : ''}`,
        data: averages,
        tooltip: {
            valueSuffix: ` ${convertedUnit || unit}`,
            valueDecimals: precision,
        },
        color: TREND_LINE_COLOR,
        boostThreshold: 0,
        marker: {
            enabled: true,
            radius: 1,
            symbol: 'circle',
            fillColor: MARKER_FILL_COLOR,
            lineWidth: 0.5,
            lineColor: TREND_LINE_COLOR,
        },
        lineWidth: 1,
        id: `${crypto.randomUUID()}`,
        yAxis: yAxisId,
    });

    const seriesName = CHART_CONDITION_TREND_KPI_TYPE;
    acc.seriesData.push({
        name: seriesName,
        type: 'column',
        boostThreshold: 0,
        lineWidth: 1,
        pointPadding: 0,
        pointRange: isHourlyDcVoltageData && !isViewWithinTwoDays ? 24 * 3600 * 1000 : 0,
        groupPadding: 0,
        borderWidth: 0,
        pointPlacement: 0.5,
        id: `${crypto.randomUUID()}`,
        yAxis: thresholdBarYAxisId,
        xAxis: 'x',
        data: getStatusBarData(conditionTrendsData, seriesName),
        enableMouseTracking: false,
        tooltip: {
            enabled: false
        },
        states: {
            inactive: {
                enabled: false
            },
            hover: {
                enabled: isDcVoltage,
                borderColor: '#000',
                borderWidth: 2
            }
        }
    });

    if (isShortTermBarVisible) {
        const shortTermSeriesName = CHART_CONDITION_TREND_SHORT_TERM_KPI_TYPE;
        acc.seriesData.push({
            name: shortTermSeriesName,
            type: 'column',
            boostThreshold: 0,
            lineWidth: 1,
            pointPadding: 0,
            groupPadding: 0,
            borderWidth: 0,
            pointPlacement: 0.5,
            id: `${crypto.randomUUID()}`,
            yAxis: shortTermBarYAxisId,
            xAxis: 'x',
            data: getStatusBarData(conditionTrendsData, shortTermSeriesName),
            enableMouseTracking: true,
            states: {
                inactive: {
                    enabled: false
                },
                hover: {
                    enabled: false
                }
            }
        });
    }

    if (isLongTermBarVisible) {
        const longTermSeriesName = CHART_CONDITION_TREND_LONG_TERM_KPI_TYPE;
        acc.seriesData.push({
            name: longTermSeriesName,
            type: 'column',
            boostThreshold: 0,
            lineWidth: 1,
            pointPadding: 0,
            groupPadding: 0,
            borderWidth: 0,
            pointPlacement: 0.5,
            id: `${crypto.randomUUID()}`,
            yAxis: longTermBarYAxisId,
            xAxis: 'x',
            data: getStatusBarData(conditionTrendsData, longTermSeriesName),
            enableMouseTracking: true,
            states: {
                inactive: {
                    enabled: false
                },
                hover: {
                    enabled: false
                }
            }
        });
    }

    acc.seriesData.push({
        name: `${name}`,
        data: rangedata,
        type: 'arearange',
        boostThreshold: 0,
        id: 'rangeSeries',
        lineWidth: 1,
        radius: 2,
        linkedTo: ':previous',
        fillOpacity: 0.3,
        opacity: 0.3,
        color: itemColor,
        zIndex: 0,
        tooltip: {
            valueSuffix: ` ${convertedUnit || unit}`,
            isRange: true
        },
        marker: {
            enabled: false,
            radius: 1,
            symbol: 'circle',
            fillColor: MARKER_FILL_COLOR,
            lineWidth: 0.2,
            lineColor: itemColor,
        },
    });
};

const processKpiData = (acc, item, index, params) => {
    if (item.kpiIdentifier === THRESHOLD_CURRENTOVERLOAD) {
        processCurrentOverload(acc, item, params.conditionTrendsData, params.conditionTrendIndicators);
        return;
    }

    const {
        userLimits, conditionTrendIndicators, unitGroupId,
        conditionTrendsData, selectedKpisDataLength
    } = params;

    const config = item.config || {};
    const unit = config?.unit || '';
    const convertedUnit = getMeasurementUnitSymbol(unitGroupId);
    const precision = config?.precision;
    const name = getTranslatedKPIName(config);
    const trendDataValues = item.values;
    const yAxisId = `y_${index}`;
    const thresholdBarYAxisId = `threshold_bar_y${index}`;
    const shortTermBarYAxisId = `short_term_bar_y${index}`;
    const longTermBarYAxisId = `long_term_bar_y${index}`;

    const { averages, rangedata } = getMeasurements(
        config, trendDataValues, selectedKpisDataLength === 1,
        conditionTrendsData, params.isViewWithinTwoDays
    );

    const isDcVoltage = config?.displayName?.toLowerCase().includes(DRIVE_DC_VOLTAGE_NAME);
    const dcVoltageStatusTrend = isDcVoltage ? conditionTrendsData?.find(trend => trend.type === CHART_CONDITION_TREND_KPI_TYPE) : null;
    const isHourlyDcVoltageData = isDcVoltage ? !!dcVoltageStatusTrend?.isHourlyData : false;
    const isCondensationRiskKpi = config?.measurementTypeKey?.includes(CONDENSATION_RISK);
    const isDcLinkCapacitorKpi = config?.measurementTypeKey?.includes(DC_LINK_CAPACITOR);

    const locals = {
        config, isDcVoltage, convertedUnit, unit, isCondensationRiskKpi,
        isDcLinkCapacitorKpi, name, itemColor: item.color, yAxisId, index, thresholdBarYAxisId,
        shortTermBarYAxisId, longTermBarYAxisId, isHourlyDcVoltageData,
        averages, precision, rangedata
    };

    acc.yAxis.push(buildMainYAxisConfig(locals, params));

    if (rangedata.length) {
        processRangeDataAndStatusBars(acc, params, locals);
    }

    acc?.seriesData?.push({
        data: getConditionIndicatorsV2(conditionTrendIndicators, isDcVoltage),
        name: TREND_TYPE_TOOLTIP_SERIES_NAME,
        color: ALERT_LINE_COLOR,
        boostThreshold: 0,
        xAxis: 'x',
        id: `${crypto.randomUUID()}`,
        yAxis: yAxisId,
        linkedTo: ':previous',
        lineWidth: 0,
        states: {
            hover: {
                lineWidthPlus: 0,
            },
        },
        marker: {
            enabled: false,
            radius: 1,
            symbol: 'circle',
            fillColor: MARKER_FILL_COLOR, // Set fill color to transparent
            lineColor: ALERT_LINE_COLOR,
            lineWidth: 0.2,
        },
        tooltip: {
            valueSuffix: ` ${convertedUnit || unit}`,
        },
    });

    acc?.seriesData?.push({
        name: CHART_SERIES_CONDITION_INDICATOR_NAME,
        id: `${crypto.randomUUID()}`,
        yAxis: yAxisId,
        boostThreshold: 0,
        lineWidth: 0,
        states: {
            hover: {
                lineWidthPlus: 0,
            },
        },
        marker: {
            enabled: false,
            radius: 1,
            symbol: 'circle',
            fillColor: MARKER_FILL_COLOR,
            lineColor: '#000000',
            lineWidth: 0.2,
        },
        xAxis: 'x',
        tooltip: {
            enabled: true,
            valueSuffix: `${convertedUnit || unit}`,
        },
        data: getConditionIndicators(conditionTrendIndicators, isDcVoltage),
    });

    addAlertLines(acc, yAxisId, params, convertedUnit, unit);

    if (userLimits?.length) {
        acc.seriesData = [
            ...acc.seriesData,
            ...getUserLimitsSeries(userLimits, config, averages),
        ];
    }
};

const getSeries = (params) => {
    const { selectedKpisData } = params;
    const ret = { yAxis: [], seriesData: [], allSeriesLength: 0 };
    const kpiParams = { ...params, selectedKpisDataLength: selectedKpisData?.length };

    transform(
        selectedKpisData,
        (acc, item, index) => {
            processKpiData(acc, item, index, kpiParams);
        },
        ret
    );

    const allSeriesLength = ret.seriesData?.length
        ? ret.seriesData.reduce(
            (count, series) => series.data?.length + count,
            0
        )
        : 0;

    return { ...ret, allSeriesLength };
};

//Forecast helper utilities

const getForecastSig = (forecast) => {
    if (forecast === null || forecast === undefined) { return FORECAST_SIG_OFF; }
    if (!Array.isArray(forecast) || !forecast.length) { return FORECAST_SIG_EMPTY; }
    return `on:${forecast.length}:${forecast[0]?.timestamp}:${forecast[forecast.length - 1]?.timestamp}`;
};

const computeHistoricalBounds = (seriesData) => {
    let historicalFirstTimestamp = null;
    let historicalLastTimestamp = null;
    (seriesData || []).forEach(series => {
        if (series?.data?.length) {
            const firstPoint = series.data[0];
            const firstXValue = Array.isArray(firstPoint) ? firstPoint[0] : firstPoint?.x;
            const lastPoint = series.data[series.data.length - 1];
            const xValue = Array.isArray(lastPoint) ? lastPoint[0] : lastPoint?.x;
            if (firstXValue && (!historicalFirstTimestamp || firstXValue < historicalFirstTimestamp)) {
                historicalFirstTimestamp = firstXValue;
            }
            if (xValue && (!historicalLastTimestamp || xValue > historicalLastTimestamp)) {
                historicalLastTimestamp = xValue;
            }
        }
    });
    return { historicalFirstTimestamp, historicalLastTimestamp };
};

const toConditionState = (value) => {
    const normalized = `${value || ''}`.trim().toLowerCase();
    if (normalized === CONDITION_STATE_POOR) { return CHART_CONDITION_TREND_STATUS_POOR; }
    if (normalized === CONDITION_STATE_TOLERABLE) { return CHART_CONDITION_TREND_STATUS_TOLERABLE; }
    if (normalized === CONDITION_STATE_GOOD) { return CHART_CONDITION_TREND_STATUS_GOOD; }
    return CONDITION_STATE_UNKNOWN;
};

const normalizeAndAlignForecast = (conditionForecast, refTimestamp) => {
    const normalized = (conditionForecast || [])
        .filter(item => item?.timestamp && item?.forecast !== undefined && item?.forecast !== null)
        .map(item => ({ ...item, ts: new Date(item.timestamp).getTime() }))
        .filter(item => !Number.isNaN(item.ts))
        .sort((a, b) => a.ts - b.ts);
    const firstForecastTs = normalized[0]?.ts;
    const shouldShift = normalized.length && refTimestamp && firstForecastTs <= refTimestamp;
    const shiftMs = shouldShift ? refTimestamp - firstForecastTs : 0;
    const alignedForecast = normalized.map(item => ({ ...item, alignedTs: item.ts + shiftMs }));
    return { alignedForecast, shiftMs };
};

const applyForecastExtensionsToSeries = (finalSeriesData, alignedForecast, yAxis, historicalLastTimestamp) => {
    if (!Array.isArray(finalSeriesData) || !Array.isArray(alignedForecast)) {
        return;
    }
    const forecastEndTs = alignedForecast[alignedForecast.length - 1]?.alignedTs || null;
    const extensionEndTs = Math.max(historicalLastTimestamp || 0, forecastEndTs || 0);
    // Continue threshold lines through forecast area
    finalSeriesData.forEach(series => {
        if (
            series.type === 'line' &&
            (series.color === ALARM_LINE_COLOR || series.color === ALERT_LINE_COLOR) &&
            Array.isArray(series.data) && series.data.length
        ) {
            const lastPt = series.data[series.data.length - 1];
            if (lastPt && typeof lastPt === 'object' && !Array.isArray(lastPt) && lastPt.x < extensionEndTs) {
                series.data = [...series.data, { x: extensionEndTs, y: lastPt.y, isForecastExtension: true }];
            }
        }
    });
    // Extend status bars in forecast region
    const forecastThresholdBarData = alignedForecast.map(item => {
        const condition = toConditionState(item?.value || null);
        return {
            y: 1, x: item?.alignedTs || null, color: statusBarColors[condition],
            events: [], condition, faultCount: 0, warningCount: 0, isFromConditionTrendsHourly: false,
        };
    });
    const forecastUnknownBarData = alignedForecast.map(item => ({
        y: 1, x: item?.alignedTs || null, color: statusBarColors.Unknown,
        events: [], condition: 'Unknown', faultCount: 0, warningCount: 0, isFromConditionTrendsHourly: false,
    }));
    finalSeriesData.forEach(series => {
        if (series?.type !== 'column' || !Array.isArray(series.data)) { return; }
        if (series.name === CHART_CONDITION_TREND_KPI_TYPE) {
            series.data = [...series.data, ...forecastThresholdBarData];
        } else if (
            series.name === CHART_CONDITION_TREND_SHORT_TERM_KPI_TYPE ||
            series.name === CHART_CONDITION_TREND_LONG_TERM_KPI_TYPE
        ) {
            series.data = [...series.data, ...forecastUnknownBarData];
        }
    });
    // Add forecast line and confidence-interval band series
    const primaryYAxis = (yAxis || []).find(y => y.id?.startsWith('y_'));
    const primaryYAxisId = primaryYAxis?.id || 'y_0';
    const primaryUnit = primaryYAxis?.labels?.format?.replace('{value} ', '') || '';
    const forecastLineId = `${crypto.randomUUID()}`;
    finalSeriesData.push(
        {
            name: translate('ABB.Powertrain.Frontend.conditionForecast'),
            data: alignedForecast.map(item => [item?.alignedTs || null, item?.forecast === null || item?.forecast === undefined ? null : roundNumber(item.forecast, 3)]),
            type: 'line', color: colors.primaryPurple, dashStyle: 'ShortDash', lineWidth: 3,
            yAxis: primaryYAxisId, xAxis: 'x', marker: { enabled: false }, zIndex: 9, boostThreshold: 0,
            id: forecastLineId,
            tooltip: { valueSuffix: ` ${primaryUnit}`, valueDecimals: 3 },
            states: { inactive: { opacity: 1 }, hover: { lineWidthPlus: 0 } },
        },
        {
            name: FORECAST_CI_SERIES_NAME,
            data: alignedForecast.map(item => [
                item?.alignedTs || null,
                item?.lowerCi === null || item?.lowerCi === undefined ? null : roundNumber(item.lowerCi, 3),
                item?.upperCi === null || item?.upperCi === undefined ? null : roundNumber(item.upperCi, 3)
            ]),
            type: 'arearange', color: colors.primaryPurpleBackground, fillColor: colors.primaryPurpleBackground,
            lineWidth: 0, fillOpacity: 1, linkedTo: forecastLineId,
            yAxis: primaryYAxisId, xAxis: 'x', id: `${crypto.randomUUID()}`,
            marker: { enabled: false }, zIndex: 8, boostThreshold: 0, enableMouseTracking: false,
            states: { inactive: { enabled: false, opacity: 1 }, hover: { enabled: false } },
            tooltip: { enabled: false },
        }
    );
};

const applyForecastAxisConfig = (chartConfig, conditionForecast, alignedForecast, sharedXAxisMax, historicalLastTimestamp) => {
    if (!chartConfig?.xAxis) { return; }

    if (conditionForecast === null) {
        chartConfig.xAxis.max = historicalLastTimestamp || null;
        chartConfig.xAxis.plotLines = [];
        chartConfig.xAxis.plotBands = [];
        return;
    }
    if (alignedForecast.length) {
        // Show forecast area strictly until available forecast data (no fixed default day window).
        chartConfig.xAxis.max = alignedForecast[alignedForecast.length - 1]?.alignedTs || null;
        // Apply shared max if another chart's forecast extends further.
        if (sharedXAxisMax && sharedXAxisMax > chartConfig.xAxis.max) {
            chartConfig.xAxis.max = sharedXAxisMax;
        }
    } else {
        // No forecast data for this KPI.
        // Always prefer sharedXAxisMax (the global right edge driven by the longest-forecast KPI)
        const ownMax = sharedXAxisMax || (historicalLastTimestamp ? historicalLastTimestamp + 24 * 3600 * 1000 : null);
        chartConfig.xAxis.max = ownMax;
        // Keep forecast side empty when no forecast data is available.
    }
    // Separator is custom-rendered only in threshold bar row (not full chart height)
    // No-data background rendered via renderer in chart render event (covers grid lines)
    chartConfig.xAxis.plotLines = [];
    chartConfig.xAxis.plotBands = [];
};

const cleanupForecastRenderElements = (chartInst) => {
    if (!chartInst) { return; }

    if (chartInst._forecastStatusSeparatorLines?.length) {
        chartInst._forecastStatusSeparatorLines.forEach(line => { line.destroy(); });
        chartInst._forecastStatusSeparatorLines = [];
    }
    if (chartInst._forecastNoDataOverlay) {
        chartInst._forecastNoDataOverlay.remove();
        chartInst._forecastNoDataOverlay = null;
    }
    if (chartInst._trendNoDataOverlay) {
        chartInst._trendNoDataOverlay.remove();
        chartInst._trendNoDataOverlay = null;
    }
    if (chartInst._forecastInfoTipEl) {
        chartInst._forecastInfoTipEl.remove();
        chartInst._forecastInfoTipEl = null;
    }
    if (chartInst._forecastInfoTipOutsideClick) {
        document.removeEventListener('click', chartInst._forecastInfoTipOutsideClick, true);
        chartInst._forecastInfoTipOutsideClick = null;
    }
    if (chartInst._forecastCrosshairHandler) {
        chartInst.container.removeEventListener('mousemove', chartInst._forecastCrosshairHandler, true);
        chartInst._forecastCrosshairHandler = null;
    }
    if (chartInst._forecastMousedownBlocker) {
        chartInst.container.removeEventListener('mousedown', chartInst._forecastMousedownBlocker, true);
        chartInst._forecastMousedownBlocker = null;
    }
};

const drawForecastSeparators = (chart, separatorX, separatorPxOverride = null) => {
    if (!chart) { return; }
    if (separatorX === null || separatorX === undefined) { return; }
    const xAx = chart.xAxis?.[0];
    const graphYAxes = chart.yAxis?.filter(ya => (ya?.options?.id || '').startsWith('y_')) || [];
    const statusYAxes = chart.yAxis?.filter(ya => {
        const axisId = ya?.options?.id || '';
        return axisId.startsWith('threshold_bar_y') || axisId.startsWith('short_term_bar_y') || axisId.startsWith('long_term_bar_y');
    }) || [];
    const targetYAxes = [...graphYAxes, ...statusYAxes];
    if (!xAx?.toPixels || !targetYAxes.length) { return; }
    const sepPx = separatorPxOverride ?? xAx.toPixels(separatorX, false);
    if (!Number.isFinite(sepPx)) { return; }
    chart._forecastStatusSeparatorLines = targetYAxes.map(axis =>
        chart.renderer
            ?.path(['M', sepPx, axis.top, 'L', sepPx, axis.top + axis.height])
            ?.attr({ stroke: colors.mineShaft, 'stroke-width': 2, zIndex: 6 })
            ?.addClass('highcharts-plot-line')
            ?.add()
    );
};

const renderForecastNoDataOverlay = (chartInst, sepPx, mainYAx, width, noForecastTitle, noForecastBody) => {
    if (!chartInst?.container || !mainYAx) { return; }

    const overlay = document.createElement('div');
    overlay.style.position = 'absolute';
    overlay.style.left = `${sepPx}px`;
    overlay.style.top = `${mainYAx.top}px`;
    overlay.style.width = `${width}px`;
    overlay.style.height = `${mainYAx.height}px`;
    overlay.style.backgroundColor = colors.wildSand;
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.textAlign = 'center';
    overlay.style.boxSizing = 'border-box';
    overlay.style.zIndex = '10';
    overlay.style.overflow = 'hidden';
    chartInst.container.style.position = 'relative';

    if (width < FORECAST_OVERLAY_NARROW_THRESHOLD) {
        // Narrow mode: centred ⓘ icon; click toggles a tooltip popup
        overlay.style.pointerEvents = 'none';
        const iconBtn = document.createElement('button');
        iconBtn.setAttribute('type', 'button');
        iconBtn.setAttribute('aria-label', noForecastTitle);
        Object.assign(iconBtn.style, {
            background: 'none', border: 'none', padding: '4px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto',
        });
        iconBtn.innerHTML = renderToStaticMarkup(<IconInfo width={16} height={16} />);

        const hideTip = () => {
            if (chartInst._forecastInfoTipEl) {
                chartInst._forecastInfoTipEl.remove();
                chartInst._forecastInfoTipEl = null;
            }
            if (chartInst._forecastInfoTipOutsideClick) {
                document.removeEventListener('click', chartInst._forecastInfoTipOutsideClick, true);
                chartInst._forecastInfoTipOutsideClick = null;
            }
        };

        const outsideClick = (ev) => {
            if (!iconBtn.contains(ev.target)) {
                hideTip();
            }
        };

        const registerOutsideClick = () => {
            document.addEventListener('click', outsideClick, true);
            chartInst._forecastInfoTipOutsideClick = outsideClick;
        };

        iconBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            // Toggle: hide if already open
            if (chartInst._forecastInfoTipEl) {
                hideTip();
                return;
            }
            // Build tooltip popup matching the dialog style
            const tip = document.createElement('div');
            Object.assign(tip.style, {
                position: 'absolute', backgroundColor: colors.white, color: colors.mineShaft,
                borderRadius: '6px', padding: '12px 14px', maxWidth: '240px',
                minWidth: '180px', fontSize: '12px', fontFamily: 'ABBvoice-Regular, sans-serif',
                lineHeight: '1.5', zIndex: '99999', pointerEvents: 'none',
                boxShadow: FORECAST_TOOLTIP_BOX_SHADOW, border: `1px solid ${colors.deionizerBorder}`,
                whiteSpace: 'normal', wordWrap: 'break-word',
            });
            tip.innerHTML =
                `<b style="display:block;margin-bottom:6px;font-weight:700;font-size:13px;color:${colors.mineShaft}">${noForecastTitle}</b>` +
                (noForecastBody ? `<span style="display:block;font-size:12px;color:${colors.doveGray}">${noForecastBody}</span>` : '') +
                `<div style="position:absolute;bottom:-9px;left:var(--tip-arrow-left, 50%);transform:translateX(-50%);width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:9px solid ${colors.deionizerBorder};z-index:-1"></div>` +
                `<div style="position:absolute;bottom:-8px;left:var(--tip-arrow-left, 50%);transform:translateX(-50%);width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:8px solid ${colors.white}"></div>`;
            chartInst.container.appendChild(tip);
            chartInst._forecastInfoTipEl = tip;
            const containerRect = chartInst.container.getBoundingClientRect();
            const btnRect = iconBtn.getBoundingClientRect();
            const containerWidth = chartInst.container.clientWidth || containerRect.width;
            const containerHeight = chartInst.container.clientHeight || containerRect.height;
            const tipW = tip.offsetWidth || 240;
            const tipH = tip.offsetHeight || 80;
            const tipLeft = Math.max(8, Math.min(
                btnRect.left - containerRect.left + btnRect.width / 2 - tipW / 2,
                containerWidth - tipW - 8
            ));
            const topCandidate = btnRect.top - containerRect.top - tipH - 12;
            const bottomCandidate = btnRect.bottom - containerRect.top + 12;
            const tipTop = topCandidate >= 8
                ? topCandidate
                : Math.min(bottomCandidate, Math.max(8, containerHeight - tipH - 8));
            tip.style.left = `${tipLeft}px`;
            tip.style.top = `${tipTop}px`;

            const iconCenterX = btnRect.left - containerRect.left + btnRect.width / 2;
            const arrowLeft = Math.max(12, Math.min(iconCenterX - tipLeft, tipW - 12));
            tip.style.setProperty('--tip-arrow-left', `${arrowLeft}px`);

            // Delay registration so the opening click does not close the tip.
            setTimeout(registerOutsideClick, 0);
        });

        overlay.appendChild(iconBtn);
    } else {
        // Wide mode: full title + body text
        overlay.style.padding = '0 20px';
        overlay.style.pointerEvents = 'none';
        const labelWidth = Math.min(width - 40, 240);
        const titleEl = document.createElement('b');
        titleEl.classList.add('trend-no-data-overlay__title');
        titleEl.textContent = noForecastTitle;

        const bodyEl = document.createElement('span');
        bodyEl.classList.add('trend-no-data-overlay__body');
        bodyEl.style.width = `${labelWidth}px`;
        bodyEl.textContent = noForecastBody;

        overlay.appendChild(titleEl);
        overlay.appendChild(bodyEl);
    }
    chartInst.container.appendChild(overlay);
    chartInst._forecastNoDataOverlay = overlay;
};

const renderTrendNoDataOverlay = (chartInst, leftPx, topPx, width, height, noTrendTitle, noTrendBody) => {
    const overlay = document.createElement('div');
    overlay.classList.add('trend-no-data-overlay');
    overlay.style.position = 'absolute';
    overlay.style.left = `${leftPx}px`;
    overlay.style.top = `${topPx}px`;
    overlay.style.width = `${width}px`;
    overlay.style.height = `${height}px`;
    chartInst.container.style.position = 'relative';

    const contentWidth = Math.max(0, width - 32);
    const titleEl = document.createElement('b');
    titleEl.classList.add('trend-no-data-overlay__title');
    titleEl.style.maxWidth = `${contentWidth}px`;
    titleEl.textContent = noTrendTitle;

    const bodyEl = document.createElement('span');
    bodyEl.classList.add('trend-no-data-overlay__body');
    bodyEl.style.maxWidth = `${contentWidth}px`;
    bodyEl.textContent = noTrendBody;

    overlay.appendChild(titleEl);
    overlay.appendChild(bodyEl);

    chartInst.container.appendChild(overlay);
    chartInst._trendNoDataOverlay = overlay;
};

const addForecastMousedownBlocker = (chartInst, separatorX) => {
    chartInst._forecastMousedownBlocker = (e) => {
        const xAx = chartInst.xAxis?.[0];
        if (!xAx?.toPixels) { return; }
        const bRect = chartInst.container.getBoundingClientRect();
        const mouseX = e.clientX - bRect.left;
        const sepPx = xAx.toPixels(separatorX, false);
        if (mouseX > sepPx) {
            e.preventDefault();
            e.stopPropagation();
        }
    };
    chartInst.container.addEventListener('mousedown', chartInst._forecastMousedownBlocker, true);
};

const addForecastCrosshairHandler = (chartInst, separatorX) => {
    chartInst._forecastCrosshairHandler = (e) => {
        const xAx = chartInst.xAxis?.[0];
        if (!xAx?.toPixels) { return; }
        const sepPx = xAx.toPixels(separatorX, false);
        const bRect = chartInst.container.getBoundingClientRect();
        const mouseX = e.clientX - bRect.left;
        if (mouseX > sepPx) {
            e.preventDefault();
            e.stopPropagation();
            xAx.hideCrosshair?.();
            chartInst.tooltip?.hide(0);
        }
    };
    chartInst.container.addEventListener('mousemove', chartInst._forecastCrosshairHandler, true);
};

const resolveDualNoDataSeparatorPx = (chartInst, separatorX, rightPx) => {
    const xAx = chartInst.xAxis?.[0];
    if (separatorX === null || separatorX === undefined || !xAx?.toPixels) {
        return null;
    }
    const candidateSepPx = xAx.toPixels(separatorX, false);
    const minPaneWidth = 140;
    const minSepPx = chartInst.plotLeft + minPaneWidth;
    const maxSepPx = rightPx - minPaneWidth;
    if (Number.isFinite(candidateSepPx) && candidateSepPx >= minSepPx && candidateSepPx <= maxSepPx) {
        return candidateSepPx;
    }
    return chartInst.plotLeft + chartInst.plotWidth / 2;
};

const renderDualNoDataOverlays = (chartInst, sepPxForDualNoData, rightPx, title) => {
    const mainYAx = chartInst.yAxis?.find(ya => ya.options?.id?.startsWith('y_'));
    if (!mainYAx || !Number.isFinite(sepPxForDualNoData)) {
        return;
    }

    const leftWidth = Math.max(0, sepPxForDualNoData - chartInst.plotLeft);
    const rightWidth = Math.max(0, rightPx - sepPxForDualNoData);
    if (leftWidth < 20 || rightWidth < 20) {
        return;
    }

    const noTrendTitle = translate('ABB.Powertrain.Frontend.chartNoMeasurementData');
    const noTrendBody = translate('ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuide');
    renderTrendNoDataOverlay(
        chartInst,
        chartInst.plotLeft,
        mainYAx.top,
        leftWidth,
        mainYAx.height,
        noTrendTitle,
        noTrendBody,
    );

    const noForecastTitle = translate('ABB.Powertrain.Frontend.noConditionForecasted');
    const noForecastBody = `${title || ''} ${translate('ABB.Powertrain.Frontend.failureForecastDetected')}`.trim();
    renderForecastNoDataOverlay(
        chartInst,
        sepPxForDualNoData,
        mainYAx,
        rightWidth,
        noForecastTitle,
        noForecastBody,
    );
};

const renderNoDataForecastOverlay = (chartInst, separatorX, rightPx, title) => {
    const mainYAx = chartInst.yAxis?.find(ya => ya.options?.id?.startsWith('y_'));
    const xAx = chartInst.xAxis?.[0];
    if (!mainYAx || !xAx?.toPixels) {
        return;
    }

    const sepPx = xAx.toPixels(separatorX, false);
    const width = Math.max(0, rightPx - sepPx);
    if (width < 20) {
        return;
    }

    const noForecastTitle = translate('ABB.Powertrain.Frontend.noConditionForecasted');
    const noForecastBody = `${title || ''} ${translate('ABB.Powertrain.Frontend.failureForecastDetected')}`.trim();
    renderForecastNoDataOverlay(chartInst, sepPx, mainYAx, width, noForecastTitle, noForecastBody);
};

const resolveChartHeight = (selectedKpisData, isShortTermBarVisible, isLongTermBarVisible) => {
    if (!selectedKpisData?.[0]?.values?.length) { return '400px'; }
    if (isShortTermBarVisible && isLongTermBarVisible) { return '600px'; }
    if (isLongTermBarVisible || isShortTermBarVisible) { return '500px'; }
    if (selectedKpisData?.[0]?.kpiIdentifier === THRESHOLD_CURRENTOVERLOAD) {
        // Only for Current Overload chart
        return '480px';
    }
    return null; // no change needed
};

const getOrderedTooltipPoints = (points, originalOrder) => {
    const orderedPoints = [];
    const pointsBySeriesId = {};
    const addedSeriesIds = new Set();

    points?.forEach(point => {
        const seriesId = point.series.options.id;
        if (seriesId) {
            pointsBySeriesId[seriesId] = point;
        }
    });

    // First, add points in the original order
    originalOrder?.forEach(seriesId => {
        if (pointsBySeriesId[seriesId]) {
            orderedPoints.push(pointsBySeriesId[seriesId]);
            addedSeriesIds.add(seriesId);
        }
    });

    points?.forEach(point => {
        const seriesId = point.series.options.id;
        if (seriesId && !addedSeriesIds.has(seriesId)) {
            orderedPoints.push(point);
            addedSeriesIds.add(seriesId);
        } else if (!seriesId) {
            orderedPoints.push(point);
        }
    });

    return orderedPoints;
};

// Cleans up the fixed status tooltip once the pointer leaves the chart (registered once per chart instance).
const attachBarTooltipMouseLeaveCleanup = (chart) => {
    if (chart._barTooltipMouseLeaveHandler) { return; }
    chart._barTooltipMouseLeaveHandler = () => {
        const tooltip = document.body.querySelector('.status-bar-tooltip-fixed');
        if (tooltip) { tooltip.remove(); }
    };
    chart.container?.addEventListener('mouseleave', chart._barTooltipMouseLeaveHandler);
};

const computeFixedTooltipPosition = (chart, plotX, tooltipWidth = 320) => {
    const chartRightEdge = chart.plotLeft + chart.plotWidth;
    const crosshairX = plotX + chart.plotLeft;
    const left = Math.min(crosshairX, chartRightEdge - tooltipWidth - 8);

    // Place tooltip just below the x-axis
    const xAxis = chart.xAxis[0];
    const xAxisBottom = xAxis.top + xAxis.height;
    const containerRect = chart.container?.getBoundingClientRect();
    const top = xAxisBottom + (containerRect?.top ?? 0);

    return { left, top };
};

const showFixedStatusTooltip = (chart, left, top, html, extraStyle) => {
    const existingStatusTooltip = document.body.querySelector('.status-bar-tooltip-fixed');
    if (existingStatusTooltip) { existingStatusTooltip.remove(); }
    if (!chart.container) { return; }

    const statusTooltipDiv = document.createElement('div');
    statusTooltipDiv.className = 'status-bar-tooltip-fixed';
    statusTooltipDiv.innerHTML = html;
    statusTooltipDiv.style.cssText = `
        position: fixed;
        left: ${left + chart.container.getBoundingClientRect().left}px;
        top: ${top}px;
        z-index: 99999;
        pointer-events: none;
        background-color: rgba(255, 255, 255, 0.9);
        box-shadow: 0 4px 16px 0 rgba(0,0,0,0.12);
        border: none;
        ${extraStyle}
    `;
    document.body.appendChild(statusTooltipDiv);
    attachBarTooltipMouseLeaveCleanup(chart);
};

const renderDcVoltageStatusBarTooltip = (chart, dcVoltageStatusBarPoint) => {
    const { left, top } = computeFixedTooltipPosition(chart, dcVoltageStatusBarPoint?.point?.plotX);
    const statusBarTooltip = renderToStaticMarkup(
        <EventsCustomTooltip
            timestamp={dcVoltageStatusBarPoint?.point?.timeStamp}
            events={dcVoltageStatusBarPoint?.point?.events}
            condition={dcVoltageStatusBarPoint?.point?.condition}
            showTimePortion={isUtcTimestamp(dcVoltageStatusBarPoint?.point?.timeStamp)}
        />
    );

    // Add the status bar tooltip to the chart container
    setTimeout(() => {
        showFixedStatusTooltip(chart, left, top, statusBarTooltip, 'border-radius: 8px; min-width: 320px; max-width: 400px;');
    }, 0);
};

const getDcVoltageTooltipMarkup = (tooltipInstance, dcVoltageMeanPoint, dcVoltageRangePoint, dcVoltageStatusBarPoint) => {
    const mainTooltip = renderToStaticMarkup(
        <DcVoltageChartToolTipComponent
            timeStamp={dcVoltageMeanPoint?.x}
            uom={dcVoltageMeanPoint?.series?.tooltipOptions?.valueSuffix?.trim()}
            isUTC={false}
            mean={dcVoltageMeanPoint?.y}
            min={dcVoltageRangePoint?.point?.low}
            max={dcVoltageRangePoint?.point?.high}
            faultCount={dcVoltageMeanPoint?.point?.faultCount}
            warningCount={dcVoltageMeanPoint?.point?.warningCount}
            events={dcVoltageMeanPoint?.point?.events}
        />
    );

    // Create status bar tooltip if status data exists
    if (dcVoltageStatusBarPoint?.point?.timeStamp) {
        renderDcVoltageStatusBarTooltip(tooltipInstance.chart, dcVoltageStatusBarPoint);
    }

    return mainTooltip;
};

const renderDcLinkCapacitorTransparentTooltip = (chart, dcLinkCapacitorPoint) => {
    const { left, top } = computeFixedTooltipPosition(chart, dcLinkCapacitorPoint?.point?.plotX);
    const messageTooltip = renderToStaticMarkup(
        <div className='dc-link-capacitor-transparent-tooltip'>
            {translate('ABB.Powertrain.Frontend.dcLinkCapacitorTransparentTooltipMessage')}
        </div>
    );

    setTimeout(() => {
        showFixedStatusTooltip(chart, left, top, messageTooltip, 'min-width: 200px; max-width: 300px;');
    }, 0);
};

const getDcLinkCapacitorTooltipMarkup = (tooltipInstance, dcLinkCapacitorPoint, orderedPoints, conditionTrendIndicators, highAlertLimitValue, timestampX) => {
    const alertLinePoint = orderedPoints?.find(p =>
        p?.series?.options?.color === ALERT_LINE_COLOR && p?.series?.type === 'line'
    );
    const rangePoint = orderedPoints?.find(p => p?.series?.type === 'arearange');
    const matchingEvent = conditionTrendIndicators?.find(
        (ci) => new Date(ci.timestamp).getTime() === timestampX
    );
    const pointUnit = dcLinkCapacitorPoint?.series?.tooltipOptions?.valueSuffix?.trim();
    const staticAlertThreshold = highAlertLimitValue?.length
        ? highAlertLimitValue[0]?.value
        : alertLinePoint?.y ?? null;
    const mainTooltip = renderToStaticMarkup(
        <DcLinkCapacitorTooltip
            timeStamp={timestampX}
            meanValue={dcLinkCapacitorPoint?.y}
            unit={pointUnit}
            min={rangePoint?.point?.low ?? dcLinkCapacitorPoint?.point?.min}
            max={rangePoint?.point?.high ?? dcLinkCapacitorPoint?.point?.max}
            alertThreshold={staticAlertThreshold}
            chargingCycleNumber={dcLinkCapacitorPoint?.point?.chargingCycleNumber}
            conditionEvent={matchingEvent}
        />
    );

    const chart = tooltipInstance.chart;
    if (dcLinkCapacitorPoint?.point?.isKpiTransparent) {
        renderDcLinkCapacitorTransparentTooltip(chart, dcLinkCapacitorPoint);
    } else {
        const existingStatusTooltip = document.body.querySelector('.status-bar-tooltip-fixed');
        if (existingStatusTooltip) { existingStatusTooltip.remove(); }
    }

    return mainTooltip;
};

const useChartConfig = (
    selectedKpisData,
    userLimits,
    yAxisTitleVisible = true,
    removeKPIData,
    warningLevels,
    warningLines,
    conditionTrendIndicators,
    highErrorValue,
    highAlertLimitValue,
    lowErrorValue,
    lowAlertValue,
    unitGroupId,
    title,
    assetDetails,
    valueType,
    conditionTrendsData,
    conditionForecast,
    sharedXAxisMax = null,
    sharedForecastSeparatorX = null,
    isZoomed = false,
) => {
    // Initialize with base config to prevent flash of default Highcharts messages
    // Use a single space for noData to disable Highcharts default "No data to display"
    const [options, setOptions] = useState({
        title: { text: null },
        lang: { noData: ' ' },
        noData: { useHTML: true },
        series: []
    });
    const prevForecastSigRef = useRef(undefined);
    const chartConfig = getChartConfig(
        removeKPIData,
        title,
        assetDetails,
        conditionTrendIndicators,
        selectedKpisData?.[0]?.config?.precision ?? DEFAULT_PRECISION
    );
    const isShortTermBarVisible = conditionTrendsData.some(kpi => kpi.type === CHART_CONDITION_TREND_SHORT_TERM_KPI_TYPE);
    const isLongTermBarVisible = conditionTrendsData.some(kpi => kpi.type === CHART_CONDITION_TREND_LONG_TERM_KPI_TYPE);

    useEffect(() => {
        const forecastSig = getForecastSig(conditionForecast);
        prevForecastSigRef.current = forecastSig;

        chartConfig.series = [];
        chartConfig.enableNoDataZoom = true;
        const allTimestamps = (selectedKpisData || []).flatMap(
            item => (item.values || []).map(v => new Date(v.timestamp).getTime())
        ).filter(Boolean);
        const dataRangeMs = allTimestamps.length >= 2
            ? Math.max(...allTimestamps) - Math.min(...allTimestamps)
            : 0;
        const isViewWithinTwoDays = dataRangeMs <= TWO_DAYS_IN_MS;

        const isDcVoltageHourlyOverTwoDays = !isViewWithinTwoDays &&
            selectedKpisData?.some(item => item?.config?.displayName?.toLowerCase().includes(DRIVE_DC_VOLTAGE_NAME)) &&
            conditionTrendsData?.find(trend => trend.type === CHART_CONDITION_TREND_KPI_TYPE)?.isHourlyData;

        const { yAxis, seriesData, allSeriesLength } = getSeries({
            selectedKpisData,
            userLimits,
            yAxisTitleVisible,
            // warningLevels,
            // warningLines,
            conditionTrendIndicators,
            highErrorValue,
            highAlertLimitValue,
            lowErrorValue,
            lowAlertValue,
            unitGroupId,
            valueType,
            conditionTrendsData,
            isShortTermBarVisible,
            isLongTermBarVisible,
            isViewWithinTwoDays,
        });

        const { historicalFirstTimestamp, historicalLastTimestamp } = computeHistoricalBounds(seriesData);

        //  Check if there's any actual measurement data, If no data exists, disable forecast completely to allow proper no-data message display
        // const hasActualData = seriesData.some(series =>
        //     series?.data &&
        //     Array.isArray(series.data) &&
        //     series.data.length > 0 &&
        //     ![
        //         CHART_SERIES_CONDITION_INDICATOR_NAME,
        //         CHART_CONDITION_TREND_KPI_TYPE,
        //         CHART_CONDITION_TREND_LONG_TERM_KPI_TYPE,
        //         CHART_CONDITION_TREND_SHORT_TERM_KPI_TYPE,
        //         TREND_TYPE_TOOLTIP_SERIES_NAME
        //     ].includes(series.name)
        // );
        // const effectiveConditionForecast = hasActualData ? conditionForecast : null;

        const refTimestamp = sharedForecastSeparatorX || historicalLastTimestamp;
        const { alignedForecast, shiftMs } = normalizeAndAlignForecast(conditionForecast, refTimestamp);

        const finalSeriesData = [...seriesData];
        if (alignedForecast.length) {
            applyForecastExtensionsToSeries(finalSeriesData, alignedForecast, yAxis, historicalLastTimestamp);
        }

        chartConfig.originalSeriesOrder = finalSeriesData?.map(series => series.id);
        chartConfig.yAxis = yAxis;
        const resolvedHeight = resolveChartHeight(selectedKpisData, isShortTermBarVisible, isLongTermBarVisible);
        if (resolvedHeight) { chartConfig.chart.height = resolvedHeight; }
        chartConfig.series = finalSeriesData;
        chartConfig.plotOptions.series.boostThreshold =
            allSeriesLength > defaultBoostThreshold ? 1 : 500;

        // Always set noData message - Highcharts will show it automatically when there's no series data
        chartConfig.lang.noData = renderToStaticMarkup(
            <ChartNoDataText
                title={translate(
                    'ABB.Powertrain.Frontend.chartNoMeasurementData'
                )}
                text={translate(
                    'ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuide'
                )}
            />
        );
        chartConfig.enableNoDataZoom = true;
        chartConfig.tooltip.shared = true;
        chartConfig.tooltip.useHTML = true;
        chartConfig.tooltip.hideDelay = 0;
        // Ensure the tooltip div always renders above the HTML no-data overlay (z-index 10)
        chartConfig.tooltip.style = { zIndex: 50 };

        const forecastStartTs = alignedForecast.length
            ? alignedForecast[0]?.alignedTs || null
            : null;
        const forecastSeparatorX = conditionForecast === null
            ? null
            : forecastStartTs || sharedForecastSeparatorX || historicalLastTimestamp || historicalFirstTimestamp || Date.now();
        chartConfig.forecastSeparatorX = forecastSeparatorX;
        chartConfig.forecastShiftMs = shiftMs;
        chartConfig.forecastNoDataRegion = conditionForecast !== null && !alignedForecast.length;

        chartConfig.xAxis.labels.formatter = function () {
            const chart = this.axis?.chart;
            const separatorTs = chart?.options?.forecastSeparatorX;
            const forecastShift = chart?.options?.forecastShiftMs || 0;
            const shiftedForecastLabel = forecastShift && separatorTs !== null && separatorTs !== undefined && this.value > separatorTs;
            const labelTs = shiftedForecastLabel ? this.value - forecastShift : this.value;
            return getXAxisLabelDateFormat(labelTs);
        };

        const hasConditionForecastData = (forecast) => {
            return Array.isArray(forecast) && forecast.length > 0;
        };
        const hasTrendMeasurements = Array.isArray(selectedKpisData) && selectedKpisData.some(
            kpi => Array.isArray(kpi?.values) && kpi.values.length > 0
        );
        const hasForecastData = hasConditionForecastData(conditionForecast);
        const isNoDataForecastRegion = conditionForecast !== null && !hasForecastData;
        const isForecastRegion = conditionForecast !== null;
        const isDualNoDataState = isForecastRegion && isNoDataForecastRegion && !hasTrendMeasurements;
        const closureForecastSeparatorX = forecastSeparatorX;

        if (isDualNoDataState) {
            // Dual no-data mode uses custom split overlays; suppress Highcharts default no-data rendering.
            chartConfig.lang.noData = ' ';
        }

        chartConfig.tooltip.positioner = function (labelWidth, labelHeight, point) {
            const chart = this.chart;
            let x = point.plotX + chart.plotLeft;
            let y = point.plotY + chart.plotTop - labelHeight - 16;

            let rightBound = chart.plotLeft + chart.plotWidth;

            // Only restrict right boundary in the no-data forecast region
            if (isNoDataForecastRegion && closureForecastSeparatorX !== null && closureForecastSeparatorX !== undefined && chart.xAxis?.[0]?.toPixels) {
                const sepPx = chart.xAxis[0].toPixels(closureForecastSeparatorX, false);
                if (sepPx !== null && sepPx !== undefined && sepPx < rightBound) {
                    rightBound = sepPx;
                }
            }

            x = Math.min(x, rightBound - labelWidth);
            x = Math.max(chart.plotLeft, x);
            y = Math.max(chart.plotTop, y);

            return { x, y };
        };

        const originalRender = chartConfig.chart.events?.render;
        chartConfig.chart.events = {
            ...chartConfig.chart.events,
            render: function (chartInstance) {
                if (originalRender) { originalRender.call(this, chartInstance); }

                // Clean up all previous forecast overlay elements before redrawing
                cleanupForecastRenderElements(this);

                if (isForecastRegion && closureForecastSeparatorX !== null && closureForecastSeparatorX !== undefined) {
                    addForecastMousedownBlocker(this, closureForecastSeparatorX);
                }

                if (isNoDataForecastRegion && closureForecastSeparatorX !== null) {
                    addForecastCrosshairHandler(this, closureForecastSeparatorX);
                }

                const rightPx = this.plotLeft + this.plotWidth;
                const sepPxForDualNoData = isDualNoDataState
                    ? resolveDualNoDataSeparatorPx(this, closureForecastSeparatorX, rightPx)
                    : null;

                // Draw separator lines in graph pane and status rows (segmented, not full-height).
                drawForecastSeparators(this, closureForecastSeparatorX, sepPxForDualNoData);

                if (isDualNoDataState) {
                    renderDualNoDataOverlays(this, sepPxForDualNoData, rightPx, title);
                    return;
                }

                if (!isNoDataForecastRegion || !closureForecastSeparatorX) { return; }
                renderNoDataForecastOverlay(this, closureForecastSeparatorX, rightPx, title);
            },
            destroy: function () {
                // Remove lingering status bar tooltip on chart destroy
                const existingStatusTooltip = document.body.querySelector('.status-bar-tooltip-fixed');
                if (existingStatusTooltip) {
                    existingStatusTooltip.remove();
                }
                // Clean up all forecast overlay elements
                cleanupForecastRenderElements(this);
            }
        };
        chartConfig.tooltip.formatter = function (tooltipInstance) {
            // Suppress tooltip only in the no-data forecast region.
            // When forecast HAS data the tooltip should work normally.
            if (isNoDataForecastRegion && closureForecastSeparatorX !== null && closureForecastSeparatorX !== undefined && this.x > closureForecastSeparatorX) {
                return '';
            }

            const originalOrder = tooltipInstance?.chart?.options?.originalSeriesOrder || [];
            const orderedPoints = getOrderedTooltipPoints(this.points, originalOrder);

            const dcVoltageMeanPoint = orderedPoints?.find(point =>
                point?.series?.name?.toLowerCase().includes(DRIVE_DC_VOLTAGE_NAME) &&
                point?.series?.type !== 'arearange'
            );
            const dcVoltageRangePoint = orderedPoints?.find(point =>
                point?.series?.type === 'arearange' &&
                point?.series?.name?.toLowerCase().includes(DRIVE_DC_VOLTAGE_NAME)
            );
            const dcVoltageStatusBarPoint = orderedPoints?.find(point =>
                point?.point?.trendType === CHART_CONDITION_TREND_KPI_TYPE &&
                point?.series?.name?.toLowerCase().includes(DRIVE_DC_VOLTAGE_NAME)
            );

            if (dcVoltageMeanPoint && dcVoltageRangePoint) {
                return getDcVoltageTooltipMarkup(tooltipInstance, dcVoltageMeanPoint, dcVoltageRangePoint, dcVoltageStatusBarPoint);
            }

            // DC Link Capacitor tooltip
            const dcLinkCapacitorPoint = orderedPoints?.find(p => p?.point?.isDcLinkCapacitor);
            if (dcLinkCapacitorPoint) {
                return getDcLinkCapacitorTooltipMarkup(
                    tooltipInstance, dcLinkCapacitorPoint, orderedPoints, conditionTrendIndicators, highAlertLimitValue, this.x
                );
            }
            const customIndicator = orderedPoints?.filter((point) => point?.series?.name === TREND_TYPE_TOOLTIP_SERIES_NAME);
            if (customIndicator && customIndicator?.length > 0) {
                const kpiPoint = orderedPoints?.length > 0 ? orderedPoints?.find(point => point?.series?.name === CHART_SERIES_CONDITION_INDICATOR_NAME) : null;
                // Find the matching indicator from conditionTrendIndicators
                const indicatorPoint = customIndicator[0]?.point;
                const indicatorTimestamp = indicatorPoint?.x;
                const matchingIndicator = conditionTrendIndicators?.find(
                    (ci) => new Date(ci.timestamp).getTime() === indicatorTimestamp
                );
                const detectedTrendX0 = matchingIndicator?.detectedTrend?.x0;
                return renderToStaticMarkup(
                    <ConditionIndicatorTooltip
                        points={customIndicator}
                        timeStamp={this.x}
                        indicatorType={customIndicator[0]?.point?.customObject}
                        kpiPoint={kpiPoint}
                        label={customIndicator[0]?.point?.KpiTypeLabel}
                        isUTC={false}
                        detectedTrendX0={detectedTrendX0} // <-- Pass x0 timestamp here
                    />
                );
            }
            // Only for Current Overload chart
            const maxLoadPoint = this.points?.find(p => p.series.name === 'Max load');
            const quadMeanPoint = this.points?.find(p => p.series.name === 'Quadratic mean load');
            if (maxLoadPoint || quadMeanPoint) {
                const date = new Date(this.x);
                const formattedDate = formatDateTime(date);
                const overloadPrecision = tooltipInstance?.chart?.options?.currentOverloadPrecision ?? DEFAULT_PRECISION;

                return renderToStaticMarkup(
                    <CurrentOverloadTooltip
                        rangeStr={formattedDate}
                        maxValue={Number(maxLoadPoint?.y).toFixed(overloadPrecision)}
                        quadMeanValue={Number(quadMeanPoint?.y).toFixed(overloadPrecision)}
                    />
                );

            }
            return renderToStaticMarkup(
                <TrendTooltip
                    points={orderedPoints
                        ?.filter(
                            (point) => ![
                                CHART_SERIES_CONDITION_INDICATOR_NAME,
                                CHART_CONDITION_TREND_KPI_TYPE,
                                CHART_CONDITION_TREND_LONG_TERM_KPI_TYPE,
                                CHART_CONDITION_TREND_SHORT_TERM_KPI_TYPE,
                                TREND_TYPE_TOOLTIP_SERIES_NAME,
                                FORECAST_CI_SERIES_NAME
                            ].includes(point?.series?.name) &&
                                !(closureForecastSeparatorX !== null &&
                                    this.x >= closureForecastSeparatorX &&
                                    point?.point?.isForecastExtension
                                )
                        )
                        .map(point => ({
                            ...point,
                            isDriveAirTemp: point?.series?.name?.toLowerCase().includes(DRIVE_AIR_TEMPERATURE_NAME)
                        }))}
                    timeStamp={this.x}
                    isUTC={false}
                />
            );
        };

        applyForecastAxisConfig(chartConfig, conditionForecast, alignedForecast, sharedXAxisMax, historicalLastTimestamp);

        if (historicalLastTimestamp) {
            chartConfig.xAxis.tickPositioner = function () {
                const ticks = this.tickPositions;
                if (!ticks?.length) { return; }

                if (!conditionForecast) {
                    const cappedTicks = ticks.filter(time => time <= historicalLastTimestamp);
                    const resultTicks = cappedTicks.length ? cappedTicks : [...ticks];
                    resultTicks.info = ticks.info;

                    return resultTicks;
                }

                const interval = ticks.length > 1 ? ticks.at(-1) - ticks.at(-2) : this.tickInterval;

                const extendedTicks = [...ticks];

                if (interval > 0) {
                    for (
                        let time = extendedTicks.at(-1) + interval;
                        time <= this.max;
                        time += interval
                    ) {
                        extendedTicks.push(time);
                    }
                }

                extendedTicks.info = ticks.info;
                return extendedTicks;
            };
        }

        if (!conditionForecast && isDcVoltageHourlyOverTwoDays) {
            chartConfig.xAxis.events = {
                ...chartConfig.xAxis.events,
                afterSetAxisTranslation() {
                    adjustAxisTranslation(this);
                }
            };
        }

        setOptions({ ...chartConfig });
    }, [
        selectedKpisData,
        userLimits,
        warningLevels,
        warningLines,
        conditionTrendIndicators,
        highErrorValue,
        highAlertLimitValue,
        valueType,
        conditionForecast,
        sharedXAxisMax,
        sharedForecastSeparatorX,
        isZoomed
    ]);

    return options;
};

export {
    getChartConfig,
    getMeasurements,
    getUserLimitsSeries,
    getThresholdLabel,
    getConditionIndicators,
    getStatusBarData,
    getPlotLinesData,
    getConditionImage,
    getConditionTypeLabel,
    getConditionIndicatorsV2,
    calculateChartHeight,
    calculateStatusBarTop,
    getSeries
};

export default useChartConfig;
